import { 
  doc, setDoc, getDoc, getDocs, collection, query, 
  runTransaction 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../utils/firebase';

const UPI_AMOUNTS = { monthly: 49, annual: 299, lifetime: 799 };
const PAYPAL_AMOUNTS = { monthly: 0.99, annual: 2.99, lifetime: 7.99 };
export const ADMIN_EMAIL = 'vishalgaikar@gmail.com';
export const PAYPAL_EMAIL = 'vishal.gaikar@yahoo.co.in';

export const isUserAdmin = (user) => {
  if (!user || !user.email) return false;
  const email = user.email.toLowerCase();
  return email === ADMIN_EMAIL || email.includes('vishalgaikar');
};

/**
 * Submits a UPI transaction reference.
 * Uses direct Firestore writes so it works immediately without CORS issues on localhost.
 * Also attempts Cloud Function if available.
 */
export const submitUpiPayment = async (planId, utr) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Please sign in before submitting your payment.');
  }

  const cleanUtr = String(utr).trim().replace(/\D/g, '').slice(0, 12);
  if (cleanUtr.length !== 12) {
    throw new Error('Please enter a valid 12-digit numeric UPI Reference No. (UTR).');
  }

  const amount = UPI_AMOUNTS[planId] || 49;
  const paymentId = cleanUtr;
  const paymentRef = doc(db, 'upiPayments', paymentId);

  // Check if document already exists
  try {
    const existingSnap = await getDoc(paymentRef);
    if (existingSnap && existingSnap.exists()) {
      const existing = existingSnap.data();
      if (existing.status === 'approved') {
        return { paymentId, status: 'approved' };
      }
      if (existing.uid && existing.uid !== user.uid) {
        throw new Error('This 12-digit UTR has already been submitted by another user.');
      }
      return { paymentId, status: existing.status || 'pending' };
    }
  } catch (checkErr) {
    if (checkErr.message && checkErr.message.includes('already been submitted')) {
      throw checkErr;
    }
    // Proceed to create if document does not exist yet
  }

  try {
    // Write directly to Firestore (Zero CORS, 100% reliable)
    await setDoc(paymentRef, {
      paymentId,
      utr: cleanUtr,
      uid: user.uid,
      email: user.email || '',
      planId,
      amount,
      currency: 'INR',
      payee: 'gaikarvishal@axl',
      status: 'pending',
      createdAt: new Date().toISOString()
    });

    return { paymentId, status: 'pending' };
  } catch (err) {
    console.warn('Direct Firestore submission error, trying callable function fallback:', err);
    // Fallback attempt via Cloud Function if deployed
    try {
      const callable = httpsCallable(functions, 'submitUpiPayment');
      const res = await callable({ planId, utr: cleanUtr });
      return res.data;
    } catch (callableErr) {
      // Return original descriptive error
      throw new Error(err.message || 'Could not record payment. Please check your network and try again.');
    }
  }
};

/**
 * Submits a direct PayPal payment reference (Transaction ID / sender email).
 * Saves to upiPayments collection so it integrates directly with the unified Admin review dashboard.
 */
export const submitPayPalPayment = async (planId, transactionId, senderEmail) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Please sign in before submitting your payment.');
  }

  const cleanTxId = String(transactionId || '').trim();
  if (!cleanTxId || cleanTxId.length < 5) {
    throw new Error('Please enter a valid PayPal Transaction ID or reference.');
  }

  const paymentId = `pp_${cleanTxId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const paymentRef = doc(db, 'upiPayments', paymentId);
  const amount = PAYPAL_AMOUNTS[planId] || 0.99;

  try {
    const snap = await getDoc(paymentRef);
    if (snap && snap.exists()) {
      const existing = snap.data();
      if (existing.status === 'approved') return { paymentId, status: 'approved' };
      if (existing.uid && existing.uid !== user.uid) {
        throw new Error('This PayPal Transaction ID has already been submitted by another user.');
      }
      return { paymentId, status: existing.status || 'pending' };
    }
  } catch (checkErr) {
    if (checkErr.message && checkErr.message.includes('already been submitted')) {
      throw checkErr;
    }
  }

  await setDoc(paymentRef, {
    paymentId,
    transactionId: cleanTxId,
    utr: cleanTxId, // compatibility for admin listing
    uid: user.uid,
    email: user.email || '',
    senderEmail: senderEmail ? String(senderEmail).trim() : (user.email || ''),
    planId,
    amount,
    currency: 'USD',
    payee: PAYPAL_EMAIL,
    paymentProvider: 'paypal',
    status: 'pending',
    createdAt: new Date().toISOString()
  });

  return { paymentId, status: 'pending' };
};

/**
 * Checks status of a payment reference.
 */
export const getUpiPaymentStatus = async (paymentId) => {
  try {
    const paymentRef = doc(db, 'upiPayments', paymentId);
    const snap = await getDoc(paymentRef);
    if (snap.exists()) {
      return { paymentId, status: snap.data().status };
    }
  } catch (e) {
    // try cloud function fallback
  }

  try {
    const callable = httpsCallable(functions, 'getUpiPaymentStatus');
    const res = await callable({ paymentId });
    return res.data;
  } catch (err) {
    throw new Error(err.message || 'Could not verify status.');
  }
};

/**
 * Fetches all UPI payment records for the Admin Dashboard.
 */
export const listUpiPayments = async (statusFilter = 'all') => {
  try {
    const q = query(collection(db, 'upiPayments'));
    const snapshot = await getDocs(q);
    let list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    if (statusFilter && statusFilter !== 'all') {
      list = list.filter(p => p.status === statusFilter);
    }
    list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return list;
  } catch (err) {
    console.warn('Direct fetch failed, trying callable fallback:', err);
    const callable = httpsCallable(functions, 'listUpiPayments');
    const res = await callable({ status: statusFilter });
    return res.data?.payments || [];
  }
};

/**
 * Approves or Rejects a UPI payment and atomically unlocks Pro for the user.
 */
export const reviewUpiPayment = async (paymentId, action, reason) => {
  if (!['approve', 'reject'].includes(action)) {
    throw new Error('Invalid action: must be approve or reject.');
  }

  const paymentRef = doc(db, 'upiPayments', paymentId);

  try {
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(paymentRef);
      if (!snap.exists()) throw new Error('Payment record not found.');
      const payment = snap.data();
      if (payment.status !== 'pending') {
        throw new Error(`Payment is already ${payment.status}.`);
      }

      if (action === 'approve') {
        const userRef = doc(db, 'users', payment.uid);
        const userSnap = await tx.get(userRef);
        const profile = userSnap.exists() ? userSnap.data() : {};
        const now = new Date();
        const existingExpiry = new Date(profile.expiryDate || 0);
        const start = existingExpiry > now ? existingExpiry : now;
        const lifetime = payment.planId === 'lifetime' || (profile.isPro && profile.plan === 'lifetime');
        const days = payment.planId === 'annual' ? 365 : 30;

        tx.set(userRef, {
          isPro: true,
          plan: lifetime ? 'lifetime' : payment.planId,
          expiryDate: lifetime ? null : new Date(start.getTime() + days * 86400000).toISOString(),
          purchaseDate: now.toISOString(),
          paymentProvider: payment.paymentProvider || 'upi',
          lastUpiPaymentId: paymentId,
          lastPaymentAt: new Date().toISOString()
        }, { merge: true });
      }

      tx.update(paymentRef, {
        status: action === 'approve' ? 'approved' : 'rejected',
        rejectionReason: action === 'reject' ? reason || null : null,
        reviewedAt: new Date().toISOString(),
        reviewedBy: auth.currentUser?.email || ADMIN_EMAIL
      });

      return { success: true, paymentId, status: action === 'approve' ? 'approved' : 'rejected' };
    });
  } catch (err) {
    console.warn('Direct Firestore review transaction failed, trying callable fallback:', err);
    const callable = httpsCallable(functions, 'reviewUpiPayment');
    const res = await callable({ paymentId, action, reason });
    return res.data;
  }
};
